/**
 * Behavior Engine
 *
 * Executa comportamentos declarativos dos campos do formulário.
 * Os behaviors vêm do FormFieldType (backend) e chegam via field.behaviors no schema.
 *
 * Tipos suportados:
 * - api_autofill: busca dados em API externa e preenche campos relacionados
 */

const API_HANDLERS = {
  /**
   * viaCEP - Consulta endereço por CEP
   * Retorna objeto com chaves: logradouro, bairro, localidade, uf, etc.
   */
  viacep: async (value) => {
    const cep = (value + '').replace(/[^\d]/g, '').slice(0, 8)
    if (cep.length < 8) return null

    const resp = await fetch(`https://viacep.com.br/ws/${cep}/json/`)
    if (!resp.ok) return null

    const data = await resp.json()
    if (data.erro) return null

    return data
  }
}

/**
 * Deduz o "escopo" do campo disparador para que os targets sejam buscados
 * no mesmo escopo (companion ou duplicata).
 *
 * - "companion_1_cep" → { prefix: "companion_1_", suffix: "" }
 * - "cep_2"           → { prefix: "", suffix: "_2" }
 * - "cep"             → { prefix: "", suffix: "" }
 */
function extractScope(triggerName) {
  if (!triggerName) return { prefix: '', suffix: '' }

  let prefix = ''
  let suffix = ''

  const prefixMatch = triggerName.match(/^(companion_\d+_)/)
  if (prefixMatch) prefix = prefixMatch[1]

  const suffixMatch = triggerName.match(/_(\d+)$/)
  if (suffixMatch) suffix = `_${suffixMatch[1]}`

  return { prefix, suffix }
}

/**
 * Encontra o campo alvo no formulário baseado numa lista de nomes possíveis.
 * Prioriza o escopo do campo disparador (prefixo companion_N_ ou sufixo _N).
 * Só cai no match global quando o trigger não tem escopo.
 */
function findTargetField(allFields, targetNames, scope = { prefix: '', suffix: '' }) {
  const { prefix, suffix } = scope

  // Se não há escopo: só match global (campo principal)
  if (!prefix && !suffix) {
    for (const name of targetNames) {
      const field = allFields.find(f => f.name === name)
      if (field) return field
    }
    return null
  }

  // Com escopo: tenta combinações em ordem de especificidade,
  // mas nunca cai no global (escapar pro principal seria o bug original).
  const candidates = []
  if (prefix && suffix) candidates.push({ prefix, suffix })
  if (prefix) candidates.push({ prefix, suffix: '' })
  if (suffix) candidates.push({ prefix: '', suffix })

  for (const c of candidates) {
    for (const name of targetNames) {
      const scoped = `${c.prefix}${name}${c.suffix}`
      const field = allFields.find(f => f.name === scoped)
      if (field) return field
    }
  }
  return null
}

/**
 * Handler para behavior tipo api_autofill
 * Quando o valor atinge match_length, chama a API e preenche campos mapeados
 */
async function handleApiAutofill(behavior, value, allFields, setFieldValue, triggerField) {
  const rawValue = (value + '')
  if (rawValue.length < (behavior.match_length || 8)) return

  const handler = API_HANDLERS[behavior.api]
  if (!handler) {
    console.warn(`[BehaviorEngine] API handler não encontrado: ${behavior.api}`)
    return
  }

  try {
    const data = await handler(value)
    if (!data) return

    const scope = extractScope(triggerField?.name)

    for (const mapping of behavior.mappings || []) {
      const targetField = findTargetField(allFields, mapping.target_names || [], scope)
      if (targetField && data[mapping.response_key] != null && data[mapping.response_key] !== '') {
        setFieldValue(targetField.name, data[mapping.response_key])
      }
    }
  } catch (e) {
    console.warn('[BehaviorEngine] Erro ao executar autofill:', e)
  }
}

/**
 * Executa todos os behaviors de um campo após mudança de valor
 *
 * @param {Object} field - Schema do campo que mudou
 * @param {*} value - Novo valor do campo
 * @param {Array} allFields - Todos os campos do formulário (preferencialmente do step atual)
 * @param {Function} setFieldValue - Callback (fieldName, value) para setar valores
 */
export async function executeBehaviors(field, value, allFields, setFieldValue) {
  if (!field?.behaviors || !Array.isArray(field.behaviors)) return

  for (const behavior of field.behaviors) {
    if (behavior.type === 'api_autofill' && behavior.trigger === 'on_complete') {
      await handleApiAutofill(behavior, value, allFields, setFieldValue, field)
    }
  }
}
